import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageCircle, X, Send, Award, Paperclip, FileText, Download } from "lucide-react";

const NAVY = "#0b192c", GOLD = "#c4a454", MUTED = "#94a3b8", BORDER = "#e2e1dd";
const POLL_MS = 8000;

function TimeStamp({ at }) {
  return (
    <div className="text-[9px] mt-0.5 opacity-60">
      {new Date(at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
    </div>
  );
}

function AttachmentPreview({ msg, downloadUrl }) {
  const a = msg?.attachment;
  if (!a) return null;
  const sizeKb = Math.max(1, Math.round((a.size || 0) / 1024));
  const isImage = ["png", "jpg", "jpeg", "webp"].includes((a.ext || "").toLowerCase());
  return (
    <a href={downloadUrl} target="_blank" rel="noreferrer"
       className="mt-1 flex items-center gap-2 rounded p-2 no-underline transition hover:opacity-90"
       style={{ background: "rgba(196,164,84,0.14)", border: "1px solid rgba(196,164,84,0.35)" }}>
      {isImage ? (
        <img src={downloadUrl} alt={a.filename} className="w-9 h-9 rounded object-cover" />
      ) : (
        <div className="w-9 h-9 rounded flex items-center justify-center"
             style={{ background: "#0b192c", color: "#c4a454" }}>
          <FileText size={16} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold truncate" style={{ color: "inherit" }}>{a.filename}</div>
        <div className="text-[9px] opacity-70">{sizeKb} KB · {(a.ext || "").toUpperCase()}</div>
      </div>
      <Download size={12} className="opacity-70" />
    </a>
  );
}

export default function ChatWidget() {
  const { user } = useAuth();
  const isDemo = !!user?.is_demo && !!user?.demo_id;

  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);

  const uploadAttachment = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (text.trim()) fd.append("caption", text.trim());
    try {
      const { data } = await api.post("/demos/chat/attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (e) {
      // Surface the error in the thread so the prospect knows the upload failed.
      const detail = e?.response?.data?.detail || "Upload failed";
      setMsgs((m) => [...m, {
        id: `err_${Date.now()}`, from_role: "system", text: `Upload failed — ${detail}`,
        at: new Date().toISOString(),
      }]);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const fetchThread = async () => {
    try {
      const { data } = await api.get("/demos/chat");
      setMsgs(data.messages || []);
      if (!open) {
        // Count admin replies the prospect hasn't seen.
        setUnread((data.messages || []).filter(
          (m) => m.from_role === "admin" && !m.read_by_prospect,
        ).length);
      } else {
        setUnread(0);
      }
    } catch { /* ignore transient errors */ }
  };

  useEffect(() => {
    if (!isDemo) return;
    fetchThread();
    const t = setInterval(fetchThread, POLL_MS);
    return () => clearInterval(t);
  }, [isDemo, open]);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [open, msgs.length]);

  if (!isDemo) return null;

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    // Optimistic append so the UI feels instant.
    const tempId = `tmp_${Date.now()}`;
    setMsgs((m) => [...m, {
      id: tempId, from_role: "prospect", text: trimmed,
      at: new Date().toISOString(), pending: true,
    }]);
    setText("");
    try {
      const { data } = await api.post("/demos/chat", { text: trimmed });
      setMsgs((m) => m.map((x) => (x.id === tempId ? data : x)));
    } catch (e) {
      setMsgs((m) => m.filter((x) => x.id !== tempId));
    }
    setSending(false);
  };

  const downloadCertificate = async () => {
    try {
      const resp = await api.get("/demos/certificate.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AIFLedger_Certificate_${(user?.name || "prospect").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[70]" data-testid="chat-widget">
      {open ? (
        <div className="w-[360px] h-[520px] max-h-[80vh] rounded-xl shadow-2xl flex flex-col overflow-hidden bg-white"
             style={{ border: `1px solid ${BORDER}` }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: NAVY, color: "white" }}>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                AIFLedger · sales
              </div>
              <div className="text-sm font-serif">We reply in under 15 minutes.</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10"
                    aria-label="Close chat" data-testid="chat-close">
              <X size={16} />
            </button>
          </div>

          {/* Certificate CTA */}
          <button onClick={downloadCertificate}
                  className="text-xs flex items-center gap-1.5 px-4 py-2 border-b hover:bg-[#faf9f5] transition"
                  style={{ borderColor: BORDER, color: NAVY }}
                  data-testid="chat-download-certificate">
            <Award size={12} style={{ color: GOLD }} /> Download your &ldquo;You&rsquo;ve toured AIFLedger&rdquo; certificate
          </button>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2"
               style={{ background: "#faf9f5" }} data-testid="chat-thread">
            {msgs.length === 0 && (
              <div className="text-xs text-center mt-6" style={{ color: MUTED }}>
                Ask us anything about AIFLedger — pricing, migration, SEBI compliance, integrations.
                Your sales contact is notified the moment you hit send.
              </div>
            )}
            {msgs.map((m) => {
              const mine = m.from_role === "prospect";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[80%] px-3 py-2 rounded-lg text-xs leading-relaxed break-words"
                       style={{
                         background: mine ? NAVY : "white",
                         color: mine ? "white" : NAVY,
                         border: mine ? "none" : `1px solid ${BORDER}`,
                         opacity: m.pending ? 0.65 : 1,
                       }}
                       data-testid={`chat-msg-${m.from_role}`}>
                    {!mine && (
                      <div className="text-[9px] uppercase tracking-widest font-semibold mb-0.5"
                           style={{ color: GOLD }}>
                        {m.from_name || "AIFLedger team"}
                      </div>
                    )}
                    <div>{m.text}</div>
                    {m.attachment && (
                      <AttachmentPreview msg={m} downloadUrl={`${api.defaults.baseURL}/demos/chat/attachments/${m.id}`} />
                    )}
                    <TimeStamp at={m.at} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="p-2 border-t flex gap-2 bg-white items-center"
                style={{ borderColor: BORDER }}>
            <button type="button" onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="p-1.5 rounded hover:bg-[#faf9f5] transition disabled:opacity-40"
                    title="Attach term sheet, deck, or PDF"
                    data-testid="chat-attach">
              <Paperclip size={14} style={{ color: uploading ? MUTED : NAVY }} />
            </button>
            <input ref={fileRef} type="file" hidden data-testid="chat-file-input"
                   accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.doc,.docx,.txt,.ppt,.pptx"
                   onChange={(e) => uploadAttachment(e.target.files?.[0])} />
            <input value={text} onChange={(e) => setText(e.target.value)}
                   placeholder={uploading ? "Uploading…" : "Type your question…"} maxLength={2000}
                   className="flex-1 border rounded px-3 py-1.5 text-xs outline-none focus:border-[#c4a454]"
                   style={{ borderColor: BORDER, color: NAVY }}
                   data-testid="chat-input" />
            <button type="submit" disabled={sending || !text.trim()}
                    className="px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
                    style={{ background: NAVY, color: "white" }}
                    data-testid="chat-send">
              <Send size={12} />
            </button>
          </form>
        </div>
      ) : (
        <button onClick={() => setOpen(true)}
                className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition relative"
                style={{ background: NAVY, color: "white" }}
                aria-label="Open chat" data-testid="chat-open">
          <MessageCircle size={22} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full flex items-center justify-center text-[10px] font-bold px-1.5"
                  style={{ background: GOLD, color: NAVY }}
                  data-testid="chat-unread-badge">
              {unread}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
